import { apiServices } from "../../../core/utilities/ApiServices";

export type RelationshipPriorityKey =
  | "relationship_intention"
  | "family_children"
  | "faith_community"
  | "lifestyle_outlook"
  | "communication_style"
  | "location_flexibility";

export interface RelationshipPriorityProfile {
  answers: Partial<Record<RelationshipPriorityKey, string | string[]>>;
  enabled: boolean;
  completed_sections: RelationshipPriorityKey[];
  total_sections: number;
}

export const multiSelectRelationshipPriorityKeys = new Set<RelationshipPriorityKey>([
  "relationship_intention",
  "lifestyle_outlook",
  "communication_style",
]);

export const relationshipPriorityValues = (
  value: string | string[] | undefined,
): string[] => (Array.isArray(value) ? value : value ? [value] : []);

export interface SharedPriority {
  key: RelationshipPriorityKey;
  value: string;
  label: string;
}

export interface RelationshipPrioritySummaries {
  summaries: Record<string, SharedPriority[]>;
  today_candidate_id: string | null;
  today_summary: SharedPriority[];
}

export const relationshipPrioritySections: Record<
  RelationshipPriorityKey,
  { title: string; icon: string; options: Record<string, string> }
> = {
  relationship_intention: {
    title: "Relationship intention",
    icon: "target",
    options: {
      lasting_relationship: "A long-term committed relationship",
      marriage: "Marriage",
      companionship: "Genuine companionship",
      friendship_first: "Friendship first, open to more",
      take_it_slow: "Build trust slowly",
    },
  },
  family_children: {
    title: "Family & children",
    icon: "family",
    options: {
      want_children: "I want children",
      open_to_children: "I am open to having children",
      dont_want_children: "I do not want children",
      have_children_want_more: "I have children and want more",
      have_children: "I have children and may want more",
      no_more_children: "I have children and do not want more",
    },
  },
  faith_community: {
    title: "Faith & community",
    icon: "faith",
    options: {
      central: "Central to my life",
      important: "Important to me",
      spiritual: "Spiritual but not religious",
      personal: "Personal and private",
      not_priority: "Not a priority",
    },
  },
  lifestyle_outlook: {
    title: "Lifestyle & outlook",
    icon: "lifestyle",
    options: {
      family_focused: "Family-focused",
      traditional: "Traditional relationship values",
      health_active: "Health and active living",
      home_focused: "Quiet and home-focused",
      community_minded: "Social and community-minded",
      balanced: "Balance and stability",
      independent: "Independent and ambitious",
    },
  },
  communication_style: {
    title: "Communication style",
    icon: "communication",
    options: {
      direct: "Direct and honest",
      thoughtful: "Thoughtful and considered",
      calm: "Calm and respectful",
      affectionate: "Warm and affectionate",
      consistent: "Frequent and consistent",
      independent: "Space and independence",
    },
  },
  location_flexibility: {
    title: "Location flexibility",
    icon: "location",
    options: {
      local: "I prefer someone local",
      local_or_distance: "Open to local or long-distance",
      distance: "Open to long-distance",
      relocate: "Open to relocating",
      travel: "Flexible about travel",
    },
  },
};

export const relationshipPrioritiesDataSource = {
  async getMe(): Promise<RelationshipPriorityProfile> {
    const response = await apiServices.get("/relationship-priorities/me");
    return response.data;
  },

  async save(
    values: Partial<Record<RelationshipPriorityKey, string | string[] | null>> & {
      enabled?: boolean;
    },
  ): Promise<RelationshipPriorityProfile> {
    const response = await apiServices.put(
      "/relationship-priorities/me",
      values,
    );
    return response.data;
  },

  async summaries(
    candidateIds: string[],
  ): Promise<RelationshipPrioritySummaries> {
    const response = await apiServices.post(
      "/relationship-priorities/summaries",
      { candidate_ids: candidateIds.slice(0, 50) },
    );
    return response.data;
  },
};
